/** @typedef {'hair' | 'skin' | 'eye' | 'eyeShine' | 'shirt' | 'shirtHighlight' | 'pants' | 'shoes' | 'outline' | 'headwear' | 'headwearAccent' | 'empty'} PixelPersonZone */

const _ = 'empty';
const o = 'outline';
const hw = 'headwear';
const ha = 'headwearAccent';

export const PIXEL_PERSON_HEADWEAR_IDS = [
  'none',
  'hardhat',
  'cap',
  'beanie',
  'bucket',
  'visor',
];

/** Headwear styles eligible for name-seeded random assignment. */
export const PIXEL_PERSON_RANDOM_HEADWEAR_IDS = PIXEL_PERSON_HEADWEAR_IDS.filter(
  (id) => id !== 'none'
);

/**
 * Field-services headwear overlays — same width as short/bob hair (rows 0–6).
 * Row 0 keeps the head outline ring only; the hat fills from row 1 down.
 */
export const PIXEL_PERSON_HEADWEAR_GRIDS = {
  hardhat: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, hw, hw, hw, hw, o, o, _, _, _],
    [_, _, o, hw, hw, hw, hw, hw, hw, hw, hw, o, _, _],
    [_, _, o, hw, hw, hw, hw, hw, hw, hw, hw, o, _, _],
    [_, o, hw, hw, hw, hw, hw, hw, hw, hw, hw, hw, o, _],
    [_, o, hw, hw, hw, ha, ha, ha, ha, hw, hw, hw, o, _],
    [_, o, hw, hw, hw, hw, hw, hw, hw, hw, hw, hw, o, _],
  ],
  cap: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, hw, hw, hw, hw, o, o, _, _, _],
    [_, _, o, hw, hw, hw, hw, hw, hw, hw, hw, o, _, _],
    [_, _, o, hw, hw, hw, hw, hw, hw, hw, hw, o, _, _],
    [_, o, hw, hw, hw, hw, hw, hw, hw, hw, hw, hw, o, _],
    [_, o, hw, hw, hw, ha, ha, ha, ha, hw, hw, hw, o, _],
  ],
  beanie: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, hw, hw, hw, hw, o, o, _, _, _],
    [_, _, o, hw, hw, hw, hw, hw, hw, hw, hw, o, _, _],
    [_, _, o, hw, hw, hw, hw, hw, hw, hw, hw, o, _, _],
    [_, o, hw, hw, hw, hw, hw, hw, hw, hw, hw, hw, o, _],
    [_, o, ha, ha, ha, ha, ha, ha, ha, ha, ha, ha, o, _],
  ],
  bucket: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, hw, hw, hw, hw, hw, hw, o, _, _, _],
    [_, _, o, hw, hw, hw, hw, hw, hw, hw, hw, o, _, _],
    [_, _, o, hw, hw, hw, hw, hw, hw, hw, hw, o, _, _],
    [_, o, hw, hw, hw, hw, hw, hw, hw, hw, hw, hw, o, _],
    [_, o, hw, hw, hw, hw, hw, hw, hw, hw, hw, hw, o, _],
    [o, hw, hw, hw, hw, hw, hw, hw, hw, hw, hw, hw, hw, o],
  ],
  visor: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, hw, hw, hw, hw, o, o, _, _, _],
    [_, _, _, o, hw, hw, hw, hw, hw, hw, o, _, _, _],
    [_, _, _, o, hw, hw, hw, hw, hw, hw, o, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, o, _, _, _, ha, ha, ha, ha, _, _, o, _, _],
  ],
};

/** Lowest row index that contains hat pixels — hair below the dome is suppressed. */
function resolveHeadwearCoverageMaxRow(headwearOverlay) {
  let maxRow = -1;
  headwearOverlay.forEach((row, rowIdx) => {
    if (row.some((zone) => zone === 'headwear' || zone === 'headwearAccent')) {
      maxRow = rowIdx;
    }
  });
  return maxRow;
}

/**
 * @param {PixelPersonZone[][]} base
 * @param {readonly (readonly PixelPersonZone[])[]} headwearOverlay
 * @returns {PixelPersonZone[][]}
 */
export function mergeHeadwearOntoGrid(base, headwearOverlay) {
  const maxHatRow = resolveHeadwearCoverageMaxRow(headwearOverlay);

  return base.map((row, rowIdx) =>
    row.map((zone, colIdx) => {
      const headwearZone = headwearOverlay[rowIdx]?.[colIdx];
      if (headwearZone && headwearZone !== 'empty') {
        return headwearZone;
      }

      // Hide hair that would poke out from under the hat dome.
      if (maxHatRow >= 0 && rowIdx <= maxHatRow && zone === 'hair') {
        return 'skin';
      }

      return zone;
    })
  );
}
